import { APIURL } from "./config";

/**
 * Exam Session API
 *
 * The assessment link carries no credential, so the backend keeps a single-active-session lock to
 * stop the same link being answered from two browsers at once. These endpoints let a recruiter see
 * which browser currently holds a candidate's assessment and release it — the case that matters
 * being a candidate sitting with the recruiter while the lock is held elsewhere.
 */

export interface ExamSession {
    id: string;
    roleAssessmentMappingId: string;
    assessmentSlotId: string | null;
    claimedAt: string;
    lastHeartbeatAt: string;
    revokedAt: string | null;
    revokedReason: string | null;
    userAgent: string | null;
    ipAddress: string | null;
    /** Claims refused while another session was actively heartbeating — the cheating signal. */
    conflictCount: number;
    /** Recoveries from a browser that had gone silent. High on a flaky connection; not a signal. */
    takeoverCount: number;
    isLive: boolean;
    /** Live but no longer reporting: the next claim takes it over automatically, no action needed. */
    isStale: boolean;
}

export interface ExamSessionsResponse {
    candidateId: string;
    /** "Off" | "Monitor" | "Enforce" — the UI says so rather than implying the lock is active. */
    enforcementMode: string;
    staleAfterSeconds: number;
    sessions: ExamSession[];
}

export const getCandidateExamSessions = async (
    candidateId: string,
    token: string
): Promise<ExamSessionsResponse | null> => {
    try {
        const res = await fetch(`${APIURL}/ExamSession/candidate/${candidateId}`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
        if (!res.ok) {
            console.error(`[getCandidateExamSessions] API error ${res.status}`);
            return null;
        }
        return await res.json();
    } catch (error) {
        console.error("[getCandidateExamSessions] fetch failed:", error);
        return null;
    }
};

/**
 * Ends every live session for a candidate (optionally scoped to one assessment) and pushes an
 * eviction to whichever browser was holding it.
 *
 * Throws on failure so the caller can surface an error toast — unlike the read above, a silent
 * failure here would leave the recruiter believing they had freed a lock that is still held.
 */
export const revokeCandidateExamSessions = async (
    candidateId: string,
    token: string,
    roleAssessmentMappingId?: string
): Promise<number> => {
    const res = await fetch(`${APIURL}/ExamSession/revoke`, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            candidateId,
            roleAssessmentMappingId: roleAssessmentMappingId ?? null,
        }),
    });
    if (!res.ok) {
        throw new Error(`Failed to end exam session: ${res.status}`);
    }
    const json = await res.json();
    return json?.revokedCount ?? 0;
};
